'use client'

import Divider from '@mui/material/Divider'
import { useState } from 'react'
import ListItemIcon from '@mui/material/ListItemIcon'
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar
} from '@mui/material'
import Link from 'next/link'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import LinkIcon from '@mui/icons-material/Link'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ReportIcon from '@mui/icons-material/Report'
import styles from './PostMenu.module.scss'
import TranslateIcon from '@mui/icons-material/Translate'
import { useTranslations } from 'next-intl'
import copyLinkService from '@/modules/copyLinkService'

interface PostMenuProps {
  editor: boolean
  postId?: string | undefined
  slug?: string
  pageUrl: string
  // eslint-disable-next-line no-unused-vars
  handleReportOption?: (id: string) => void
}

function PostMenu ({ editor, postId, pageUrl, handleReportOption, slug }: PostMenuProps) {
  const t = useTranslations('PostMenu')
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null)
  const [message, toggleMessage] = useState(false)
  const shareOpen = Boolean(shareAnchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShareAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setShareAnchorEl(null)
  }

  return (
    <>
      <Menu
        id="basic-menu"
        anchorEl={shareAnchorEl}
        open={shareOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        sx={{
          '& .MuiMenu-paper': {
            background: 'var(--primary-chip-bg)',
            color: 'var(--primary-font-rgb)'
          }
        }}
      >
        <MenuItem
          dense
          style={{ minWidth: '11rem' }}
          onClick={() => {
            copyLinkService.notifyServerAndCopy(postId || '', pageUrl)
            toggleMessage(true)
            handleClose()
          }}
        >
          <ListItemIcon
            sx={{
              color: 'var(--primary-chip-color)',
              opacity: 0.7
            }}
          >
            <LinkIcon
              fontSize='large'
            />
          </ListItemIcon>
          {t('copy-link')}
        </MenuItem>
        {slug && <Divider style={{ margin: '0.5rem 0' }} />}
        {slug &&
          <Link href={`/${slug}/translations`}>
            <MenuItem
              dense
              onClick={() => {
                //
                handleClose()
              }}
            >
              <ListItemIcon>
                <TranslateIcon
                  className={styles.linkIcon}
                  fontSize='large'
                />
              </ListItemIcon>
              {t('translations')}
            </MenuItem>
          </Link>
        }
        {editor &&
          <Divider style={{ margin: '0.5rem 0' }} />
        }
        {editor &&
          <Link href={`/drafts/${postId}`}>
            <MenuItem onClick={handleClose}>
              <ListItemIcon
                sx={{
                  color: 'var(--primary-chip-color)',
                  opacity: 0.7
                }}
              >
                <EditNoteIcon fontSize="small" />
              </ListItemIcon>
              {t('edit')}
            </MenuItem>
          </Link>
        }
        <Divider style={{ margin: '0.5rem 0' }} />
        <MenuItem
          dense
          style={{ minWidth: '11rem' }}
          onClick={() => {
            handleClose() // close the menu
            // open the report post dialog
            handleReportOption && handleReportOption(postId as string)
          }}
        >
          <ListItemIcon
            sx={{
              color: 'var(--primary-chip-color)',
              opacity: 0.7
            }}
          >
            <ReportIcon fontSize="small" />
          </ListItemIcon>
          {t('report')}
        </MenuItem>
      </Menu>
      <Tooltip
        title={t('options')}
        aria-label='options-button'
        id="basic-button"
      >
        <IconButton
          onClick={handleClick}
          size='medium'
          style={{ padding: 0 }}
        >
          <MoreHorizIcon className={styles.shareIcon} />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={message}
        autoHideDuration={6000}
        onClose={() => toggleMessage(false)}
        message={t('copied')}
      />
    </>
  )
}

export default PostMenu
